import React, { useState, useEffect } from 'react';
import {
  ExternalLink,
  Camera,
  MapPin,
  ShieldCheck,
  FileText,
  MessageCircle,
  Sparkles,
} from 'lucide-react';
import {
  TELEGRAM_BOT_USERNAME,
  TELEGRAM_CHAT_URL,
  TELEGRAM_ASSISTANT_NAME,
  TELEGRAM_ASSISTANT_TITLE,
} from '../config/telegram';

export const TelegramIcon: React.FC<{ className?: string }> = ({ className = 'w-5 h-5' }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 00-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.12-.31-1.08-.66.02-.18.27-.36.75-.55 2.92-1.27 4.86-2.11 5.83-2.51 2.78-1.16 3.35-1.36 3.73-1.36.08 0 .27.02.39.12.1.08.13.19.14.27-.01.06.01.24 0 .38z" />
  </svg>
);

interface TelegramAssistantSectionProps {
  className?: string;
}

export const TelegramAssistantSection: React.FC<TelegramAssistantSectionProps> = ({
  className = '',
}) => {
  const [botUsername, setBotUsername] = useState<string>(TELEGRAM_BOT_USERNAME);
  const [telegramUrl, setTelegramUrl] = useState<string>(TELEGRAM_CHAT_URL);

  useEffect(() => {
    let isMounted = true;
    fetch('/api/telegram/config')
      .then((res) => {
        if (!res.ok) return null;
        return res.json();
      })
      .then((data) => {
        if (isMounted && data && data.success && data.botUsername) {
          setBotUsername(data.botUsername);
          setTelegramUrl(data.telegramUrl || `https://t.me/${data.botUsername}`);
        }
      })
      .catch((err) => {
        console.info('Telegram config sync note:', err);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const handleStartChat = () => {
    window.open(telegramUrl, '_blank', 'noopener,noreferrer');
  };

  const features = [
    {
      icon: <MessageCircle className="w-4 h-4 text-sky-400 shrink-0" />,
      text: 'Describe your problem directly in Telegram',
    },
    {
      icon: <Camera className="w-4 h-4 text-sky-400 shrink-0" />,
      text: 'Send photos or videos of the problem',
    },
    {
      icon: <Sparkles className="w-4 h-4 text-sky-400 shrink-0" />,
      text: 'AI assistant asks follow-up questions when necessary',
    },
    {
      icon: <MapPin className="w-4 h-4 text-sky-400 shrink-0" />,
      text: 'Your location/LGA is used for professional matching',
    },
    {
      icon: <ShieldCheck className="w-4 h-4 text-sky-400 shrink-0" />,
      text: 'Only verified and active professionals receive eligible job requests',
    },
    {
      icon: <FileText className="w-4 h-4 text-sky-400 shrink-0" />,
      text: 'Receive and compare professional responses and quotations',
    },
  ];

  return (
    <section className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${className}`}>
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900 via-sky-950 to-slate-900 border border-sky-800/40 p-6 sm:p-10 text-white shadow-2xl">
        {/* Subtle Ambient Decorative Gradient Rings */}
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-sky-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />

        <div className="relative z-10 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8 lg:gap-12">
          {/* Main Copy & Header */}
          <div className="space-y-4 max-w-2xl">
            {/* Header Badge */}
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-sky-500/20 border border-sky-400/30 text-sky-300 text-xs font-bold">
              <TelegramIcon className="w-4 h-4 text-sky-400" />
              <span>{TELEGRAM_ASSISTANT_NAME}</span>
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-[11px] text-emerald-300 font-semibold">{TELEGRAM_ASSISTANT_TITLE}</span>
            </div>

            <div className="space-y-2">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-white tracking-tight">
                {TELEGRAM_ASSISTANT_NAME}
              </h2>
              <p className="text-sm sm:text-base font-bold text-sky-300">
                {TELEGRAM_ASSISTANT_TITLE}
              </p>
            </div>

            <p className="text-xs sm:text-sm text-slate-300 leading-relaxed">
              Need a plumber, electrician, carpenter, painter, tiler, bricklayer, or other professional?
            </p>

            <p className="text-xs sm:text-sm text-slate-300/95 leading-relaxed">
              Send a message on Telegram and our AI assistant will understand your problem, ask the necessary
              questions, collect your location and job details, and connect your request with verified active
              professionals in your LGA.
            </p>

            {/* Features List */}
            <div className="pt-2">
              <h4 className="text-[11px] font-black uppercase tracking-wider text-sky-400 mb-2.5">
                Features:
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {features.map((feature, idx) => (
                  <div key={idx} className="flex items-start gap-2.5 text-xs text-slate-200">
                    <div className="mt-0.5">{feature.icon}</div>
                    <span className="leading-snug">{feature.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Action Box / CTA Card */}
          <div className="w-full lg:w-auto shrink-0 flex flex-col items-center lg:items-end gap-3.5">
            <div className="w-full sm:w-80 bg-white/5 backdrop-blur-md rounded-2xl p-5 border border-white/10 text-center space-y-3">
              <div className="w-12 h-12 mx-auto rounded-2xl bg-sky-500/20 border border-sky-400/30 flex items-center justify-center text-sky-400">
                <TelegramIcon className="w-7 h-7" />
              </div>
              <div>
                <p className="text-xs font-bold text-white">Instant AI Booking via Telegram</p>
                <p className="text-[11px] text-sky-300 font-mono mt-0.5">@{botUsername}</p>
              </div>

              <a
                href={telegramUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => {
                  e.preventDefault();
                  handleStartChat();
                }}
                className="w-full py-3.5 px-6 rounded-xl bg-sky-500 hover:bg-sky-400 text-slate-950 font-black text-sm transition-all flex items-center justify-center gap-2.5 shadow-lg shadow-sky-500/25 hover:shadow-sky-400/40 cursor-pointer min-h-[48px]"
              >
                <TelegramIcon className="w-5 h-5 fill-slate-950" />
                <span>Start Telegram Chat</span>
                <ExternalLink className="w-4 h-4 opacity-70 ml-0.5" />
              </a>

              <p className="text-[10px] text-slate-400">
                Opens in Telegram app or web browser • Free to use
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
